import React from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    TableSortLabel,
} from "@mui/material";
import {
    getComparator,
    stableSort,
} from "../../../common-utilities/sortingUtils";

const EtkOrdersReportTable = ({ ordersData, count, onChange, baseUrl }) => {
    const columns = [
        "Order#",
        "Account#",
        "Date Received",
        "Project Description",
        "Status",
    ];
    const columnHeaders = [
        "orderNumber",
        "accountNumber",
        "dateReceived",
        "projectDescription",
        "orderStatus",
    ];
    const rowsPerPageOptions = [20, 50, 100, 150, 200, 400, 600, 800, 1000];

    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(rowsPerPageOptions[1]);
    const [orderBy, setOrderBy] = React.useState("");
    const [order, setOrder] = React.useState("asc");

    React.useEffect(() => {
        onChange(page + 1, rowsPerPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "";
        const options = { year: "numeric", month: "2-digit", day: "2-digit" };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const getOrderUrl = (orderNumber) =>
        `${baseUrl}/vlp/edit/lab-orders/${orderNumber}`;

    const getAccountUrl = (accountNumber) =>
        `${baseUrl}/vlp/edit-client/${accountNumber}`;

    const handleViewOrderClick = (orderNumber) => {
        const orderUrl = getOrderUrl(orderNumber);
        if (window.labOrderViewWindow && !window.labOrderViewWindow.closed) {
            window.labOrderViewWindow.location.href = orderUrl;
        } else {
            window.labOrderViewWindow = window.open(
                orderUrl,
                "_blank",
                "resizable=yes,scrollbars=yes"
            );
        }
    };

    const handleViewAccountClick = (accountNumber) => {
        const accountUrl = getAccountUrl(accountNumber);
        if (window.accountViewWindow && !window.accountViewWindow.closed) {
            window.accountViewWindow.location.href = accountUrl;
        } else {
            window.accountViewWindow = window.open(
                accountUrl,
                "_blank",
                "resizable=yes,scrollbars=yes"
            );
        }
    };

    const sortedData = stableSort(ordersData, getComparator(order, orderBy));

    return (
        <Paper style={{ marginRight: "20px" }}>
            <TableContainer sx={{ maxHeight: 500, overflowY: "auto", width: "99%" }}>
                <Table dense stickyHeader>
                    <TableHead
                        sx={{
                            backgroundColor: (theme) => theme.palette.primary.main,
                            color: "white",
                        }}
                    >
                        <TableRow>
                            {columns.map((column, index) => (
                                <TableCell
                                    key={column}
                                    onClick={() => handleRequestSort(columnHeaders[index])}
                                >
                                    <TableSortLabel
                                        active={orderBy === columnHeaders[index]}
                                        direction={
                                            orderBy === columnHeaders[index] ? order : "asc"
                                        }
                                    >
                                        {column}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedData.map((order, index) => (
                            <TableRow
                                key={order.orderNumber ?? index}
                                sx={{
                                    backgroundColor:
                                        index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                                }}
                            >
                                {columnHeaders.map((column) => (
                                    <TableCell
                                        key={column}
                                        sx={
                                            column === "orderNumber" || column === "accountNumber"
                                                ? {
                                                    cursor: "pointer",
                                                    color: "blue",
                                                    textDecoration: "underline",
                                                }
                                                : {}
                                        }
                                        onClick={
                                            column === "orderNumber"
                                                ? () => handleViewOrderClick(order.orderNumber)
                                                : column === "accountNumber"
                                                    ? () => handleViewAccountClick(order.accountNumber)
                                                    : undefined
                                        }
                                    >
                                        {column === "dateReceived"
                                            ? formatDate(order[column])
                                            : order[column]}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={rowsPerPageOptions}
                component="div"
                count={count}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
    );
};

export default EtkOrdersReportTable;